/**
 * Auto Fibonacci — picks the swing a retracement should be anchored to, so the
 * chart can place one without the user clicking two pivots.
 *
 * Pure: bars in, a pair of anchors out. The chart maps the pair to the
 * `autoFibonacci` overlay (see `fibonacciOverlay.ts`).
 *
 * Which swing: the MOST SIGNIFICANT one inside a lookback window, i.e. the
 * highest high and the lowest low of the window, ordered in time. The earlier
 * extreme is the leg's start (100%) and the later one its end (0%), so the
 * retracement levels measure how far price has given back of the biggest move
 * the window holds. The window is the caller's choice (`lookbackBars`);
 * `autoFibLookbackBars` derives it per timeframe: the current session for
 * intraday charts (a 2-minute chart must see the whole day, not the last half
 * hour), about six months of daily bars, two years of weekly bars. The swing
 * must span at least `swingMult` ATRs or nothing is drawn.
 *
 * `touchInput` decides whether the extremes are read off wicks or candle
 * bodies, the same dial every other auto-analysis detector uses. ATR stays on
 * the raw wicks either way.
 */

import type { EdgeBar } from '../types/chart'
import { touchAccessors, type Swing, type TouchInput } from './patternDetection'
import { trueRangeSeries, wilderSeries } from './seriesMath'

export interface AutoFibOptions {
  /** ATR period for the size gate. Default 14. */
  atrPeriod?: number
  /** Minimum swing size (high minus low), in ATR multiples. Default 3. */
  swingMult?: number
  /** Wicks or candle bodies as the extreme series. Default 'wicks'. */
  touchInput?: TouchInput
  /** How many of the most recent bars to search. Default: all of them.
   *  ChartManager passes `autoFibLookbackBars(bars, period)`. */
  lookbackBars?: number
}

export interface AutoFibSwing {
  /** Where the leg started (100% level). */
  start: Swing
  /** Where the leg currently ends (0% level). */
  end: Swing
}

function medianAtr(bars: readonly EdgeBar[], atrPeriod: number): number {
  const vals = wilderSeries(trueRangeSeries(bars), atrPeriod).filter(
    (a): a is number => a != null && a > 0,
  )
  if (!vals.length) return bars[bars.length - 1].close * 0.01
  const s = [...vals].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/**
 * The swing pair to anchor the auto Fib to, or `null` when the window holds no
 * swing of at least `swingMult` ATRs (or fewer than two bars). Ties between
 * equal extremes go to the most recent bar, so the anchor reads as current.
 */
export function pickAutoFibSwing(
  bars: readonly EdgeBar[],
  opts: AutoFibOptions = {},
): AutoFibSwing | null {
  const { atrPeriod = 14, swingMult = 3, touchInput = 'wicks', lookbackBars } = opts
  const n = bars.length
  if (n < 2) return null
  const from = lookbackBars != null && lookbackBars > 0 ? Math.max(0, n - lookbackBars) : 0
  if (n - from < 2) return null
  const { hi, lo } = touchAccessors(touchInput)
  let hiIdx = from
  let loIdx = from
  let hiVal = hi(bars[from])
  let loVal = lo(bars[from])
  for (let i = from + 1; i < n; i++) {
    const h = hi(bars[i])
    const l = lo(bars[i])
    if (h >= hiVal) {
      hiVal = h
      hiIdx = i
    }
    if (l <= loVal) {
      loVal = l
      loIdx = i
    }
  }
  if (hiIdx === loIdx) return null
  // ATR over the whole loaded history, not just the window: a short window
  // (the first minutes of a session) cannot warm a 14-period ATR up.
  const atr = medianAtr(bars, atrPeriod)
  const range = hiVal - loVal
  if (!(atr > 0) || !(range >= swingMult * atr)) return null
  const mk = (index: number, price: number, kind: 'high' | 'low'): Swing => ({
    index,
    timestamp: bars[index].timestamp,
    price,
    kind,
    magnitude: range / atr,
  })
  const high = mk(hiIdx, hiVal, 'high')
  const low = mk(loIdx, loVal, 'low')
  return hiIdx < loIdx ? { start: high, end: low } : { start: low, end: high }
}

const ET_DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Calendar day in New York for a bar timestamp (the exchange's day). */
export function etDayKey(ts: number): string {
  return ET_DAY.format(new Date(ts))
}

/**
 * Lookback per timeframe. Intraday: every bar of the last bar's New York
 * calendar day (premarket included), extended back a day at a time while the
 * window holds fewer than `minIntradayBars` bars, so the first minutes of a
 * session still see yesterday's structure and a gap day is measured against
 * where price came from. Daily: 120 bars (about six months). Weekly: 104
 * (two years). Monthly and up: 60.
 */
export function autoFibLookbackBars(
  bars: readonly EdgeBar[],
  period: { timespan: string },
  minIntradayBars = 60,
): number {
  const n = bars.length
  if (n === 0) return 0
  if (period.timespan === 'minute' || period.timespan === 'hour') {
    let count = 0
    let day = etDayKey(bars[n - 1].timestamp)
    let days = 1
    for (let i = n - 1; i >= 0; i--) {
      const k = etDayKey(bars[i].timestamp)
      if (k !== day) {
        if (count >= minIntradayBars || days >= 5) break
        day = k
        days++
      }
      count++
    }
    return count
  }
  if (period.timespan === 'day') return 120
  if (period.timespan === 'week') return 104
  return 60
}

/** Overlay points for the pair: `points[0]` is the 0% anchor, `points[1]` the
 *  100% anchor (the `fibonacciLine` geometry). */
export function autoFibPoints(pair: AutoFibSwing): Array<{ timestamp: number; value: number }> {
  return [
    { timestamp: pair.end.timestamp, value: pair.end.price },
    { timestamp: pair.start.timestamp, value: pair.start.price },
  ]
}
